import React from 'react';
import { IoSearch, IoDocumentText, IoCodeSlash, IoRocket, IoCheckmarkDone } from 'react-icons/io5';
import AnimatedSection from '../ui/AnimatedSection';
import { motion } from 'framer-motion';

const Process = () => {
  const steps = [
    {
      icon: <IoSearch className="text-4xl" />,
      title: "Discovery & Analysis",
      description: "We begin by understanding your business goals, challenges, and requirements. Our team conducts thorough analysis to create a strategic roadmap.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <IoDocumentText className="text-4xl" />,
      title: "Planning & Design",
      description: "We design comprehensive solutions tailored to your needs. This includes architecture planning, UI/UX design, and technical specifications.",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <IoCodeSlash className="text-4xl" />,
      title: "Development & Testing",
      description: "Our expert developers build your solution using agile methodologies. Continuous testing ensures quality at every stage of development.",
      color: "from-green-500 to-green-600"
    },
    {
      icon: <IoRocket className="text-4xl" />,
      title: "Deployment & Launch",
      description: "We handle seamless deployment and launch your solution. Our team ensures zero downtime and smooth transition to production.",
      color: "from-orange-500 to-orange-600"
    },
    {
      icon: <IoCheckmarkDone className="text-4xl" />,
      title: "Support & Maintenance",
      description: "Post-launch, we provide ongoing support, monitoring, and updates. We ensure your solution continues to perform optimally.",
      color: "from-teal-500 to-teal-600"
    }
  ];

  return (
    <section id="process" className="py-20 dark-section relative">
      <div className="container mx-auto px-4 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-8">
            <h2 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-2">Our Process</h2>
            <p className="text-3xl md:text-4xl font-bold text-white">How We Work</p>
          </div>
        </AnimatedSection>
        <AnimatedSection delay={0.2}>
          <p className="text-center text-gray-300 max-w-3xl mx-auto mb-12">
            Our proven methodology ensures transparent communication, timely delivery, and exceptional results at every stage of your project.
          </p>
        </AnimatedSection>
        
        <div className="relative mt-12">
          {/* Connection Line */}
          <motion.div 
            className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 via-green-500 via-orange-500 to-teal-500 opacity-30"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
            style={{ transformOrigin: "left" }}
          ></motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ 
                  duration: 0.5, 
                  delay: index * 0.15,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                whileHover={{ y: -8, scale: 1.02 }}
              >
                <div className={`bg-gradient-to-br ${step.color} rounded-xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-300 h-full`}>
                  <motion.div 
                    className="flex justify-center mb-4"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <div className="bg-white bg-opacity-20 rounded-full p-4">
                      {step.icon}
                    </div>
                  </motion.div>
                  <div className="text-center mb-2">
                    <span className="bg-white bg-opacity-30 rounded-full px-3 py-1 text-sm font-bold">
                      Step {index + 1}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-center">{step.title}</h3>
                  <p className="text-white text-opacity-90 text-sm text-center">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;

